import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useLiveCoachFeed } from '../../../contexts/LiveCoachFeedContext';

/**
 * "Atletas en vivo" widget. Subscribes to the /sessions socket via
 * useLiveCoachFeed and renders the active sessions. Empty state when nobody
 * is training. Click → navigates to the client dashboard.
 */
const LiveAtletasWidget = () => {
  const navigate = useNavigate();
  const { liveSessions, connected } = useLiveCoachFeed();

  return (
    <div
      style={{
        background: 'var(--ios-card-bg)',
        border: '1px solid var(--ios-card-border)',
        borderRadius: 'var(--ios-radius-lg)',
        padding: '1rem 1.1rem',
        boxShadow: 'var(--ios-shadow-sm)',
        minHeight: '180px'
      }}
    >
      <div className="flex align-items-center justify-content-between mb-3">
        <strong style={{ fontSize: '0.95rem', color: 'var(--ios-text)' }}>
          <FormattedMessage id="coach.liveAtletas.title" defaultMessage="Atletas en vivo" />
        </strong>
        <div className="flex align-items-center gap-2">
          {liveSessions.length > 0 && (
            <span
              style={{
                fontSize: '0.72rem',
                color: '#15803d',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {liveSessions.length}
            </span>
          )}
          <span
            title={connected ? 'Conectado al servidor' : 'Sin conexión'}
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: connected ? '#22c55e' : '#a3a3a3',
              boxShadow: connected ? '0 0 8px #22c55e' : undefined,
              opacity: 0.5
            }}
            aria-hidden
          />
        </div>
      </div>

      {liveSessions.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '1.5rem 0',
            color: 'var(--ios-text-tertiary)',
            fontSize: '0.85rem'
          }}
        >
          <FormattedMessage
            id="coach.liveAtletas.empty"
            defaultMessage="Nadie está entrenando ahora. Cuando un atleta abra su sesión vas a verlo acá."
          />
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {liveSessions.map((s) => (
            <li
              key={s.clientId}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/client-dashboard/${s.clientId}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') navigate(`/client-dashboard/${s.clientId}`);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 'var(--ios-radius-sm)',
                background: s.status === 'live' ? 'rgba(34,197,94,0.10)' : 'var(--ios-surface-subtle)',
                cursor: 'pointer',
                transition: 'background 0.15s'
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg,#a3e635,#65a30d)',
                  display: 'grid',
                  placeItems: 'center',
                  color: '#0a0a0a',
                  fontWeight: 700,
                  fontSize: '0.78rem'
                }}
                aria-hidden
              >
                {(s.clientName ?? `C${s.clientId}`).slice(0, 2).toUpperCase()}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--ios-text)' }}>
                  {s.clientName ?? `Cliente #${s.clientId}`}
                </div>
                <div
                  style={{
                    fontSize: '0.72rem',
                    color: s.status === 'live' ? '#15803d' : 'var(--ios-text-tertiary)',
                    fontWeight: 500
                  }}
                >
                  {s.status === 'completed' ? (
                    <FormattedMessage id="coach.liveAtletas.completed" defaultMessage="Sesión completada" />
                  ) : s.exerciseName ? (
                    <>
                      {s.exerciseName}
                      {s.setNumber && s.totalSets ? ` · set ${s.setNumber}/${s.totalSets}` : ''}
                    </>
                  ) : (
                    <FormattedMessage id="coach.liveAtletas.opened" defaultMessage="Abrió la sesión" />
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LiveAtletasWidget;
